/**
 * Expert-audited preference questionnaire.
 *
 * User-facing form (7 questions, 5 mandatory). Answers are persisted per
 * user via POST /api/expert-preferences and pre-filled from GET
 * /api/expert-preferences on load. Admins can see everyone's
 * submissions from the /admin panel; users only ever see their own.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, ClipboardList, Check, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { expertPrefsApi } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { format } from 'date-fns';

const ATTRACT_OPTIONS = [
  { key: 'recruiters', label: 'Recruiters' },
  { key: 'prospects_clients', label: 'Prospects & clients' },
  { key: 'industry_peers', label: 'Industry peers' },
];

const TONE_OPTIONS = [
  { key: 'formal', label: 'Formal', hint: 'Precise, corporate, understated' },
  { key: 'authoritative', label: 'Authoritative', hint: 'Confident, direct, expert-led' },
  { key: 'conversational', label: 'Conversational', hint: 'Warm, plain-spoken, human' },
  { key: 'creative', label: 'Creative', hint: 'Vivid, story-driven, unconventional' },
  { key: 'approachable', label: 'Approachable', hint: 'Friendly, inviting, humble' },
];

const VISITOR_ACTIONS = [
  { key: 'connect', label: 'Send a connection request' },
  { key: 'message', label: 'Send a direct message' },
  { key: 'visit_website', label: 'Visit my website' },
  { key: 'download_portfolio', label: 'Download my portfolio' },
  { key: 'book_call', label: 'Book a call' },
];

const REQUIRED_LABEL = (
  <span className="ml-1 text-destructive font-medium" aria-label="required" title="Required">*</span>
);

function SkillsInput({ value, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return;
    if (value.some((v) => v.toLowerCase() === s.toLowerCase())) {
      setDraft('');
      return;
    }
    if (value.length >= 10) {
      toast.error('You can add up to 10 skills.');
      return;
    }
    onChange([...value, s.slice(0, 80)]);
    setDraft('');
  };
  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeAt(value.length - 1);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[36px] rounded-lg border border-input bg-background p-2" data-testid={`${testId}-chip-list`}>
        {value.map((s, i) => (
          <Badge key={`${s}-${i}`} variant="secondary" className="rounded-full pl-3 pr-1 py-1 text-xs" data-testid={`${testId}-chip-${i}`}>
            {s}
            <button
              type="button"
              className="ml-1.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${s}`}
              data-testid={`${testId}-remove-${i}`}
            >
              <X size={11} />
            </button>
          </Badge>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground px-1 py-0.5">Add up to 10 skills — press Enter or comma to lock each one in.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="e.g. Product-led growth"
          maxLength={80}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => add(draft)}
          disabled={!draft.trim() || value.length >= 10}
          data-testid={`${testId}-add-btn`}
        >
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {value.length} / 5 minimum recommended · {value.length}/10 total
      </p>
    </div>
  );
}

function RadioTiles({ options, value, onChange, testId, columns = 3 }) {
  const gridCls = columns === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-3';
  return (
    <div className={`grid ${gridCls} gap-2`} data-testid={testId}>
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => onChange(opt.key)}
            data-testid={`${testId}-${opt.key}`}
            className={`text-left rounded-xl border p-3 transition-all ${active
              ? 'border-primary bg-primary/5 shadow-sm'
              : 'border-border bg-card hover:border-primary/40 hover:bg-secondary/40'
            }`}
          >
            <div className="flex items-start gap-2">
              <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-input'}`}>
                {active && <Check size={10} strokeWidth={3} />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{opt.label}</p>
                {opt.hint && <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{opt.hint}</p>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}


export default function PreferencesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ submitted_at: null, updated_at: null });

  const [attract, setAttract] = useState('');
  const [targeting, setTargeting] = useState('');
  const [skills, setSkills] = useState([]);
  const [stories, setStories] = useState('');
  const [problems, setProblems] = useState('');
  const [tone, setTone] = useState('');
  const [actions, setActions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await expertPrefsApi.getMine();
        if (cancelled) return;
        const a = r?.answers;
        if (a) {
          setAttract(a.attract_audience || '');
          setTargeting(a.target_industries_titles || '');
          setSkills(Array.isArray(a.top_skills) ? a.top_skills : []);
          setStories(a.success_stories || '');
          setProblems(a.problems_solved || '');
          setTone(a.tone || '');
          setActions(Array.isArray(a.visitor_actions) ? a.visitor_actions : []);
        }
        setMeta({ submitted_at: r?.submitted_at || null, updated_at: r?.updated_at || null });
      } catch (err) {
        toast.error(getErrorMessage(err, 'Could not load your questionnaire'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleAction = (key) => {
    setActions((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const validationError = useMemo(() => {
    if (!attract) return 'Q1: pick who you want to attract.';
    if (targeting.trim().length < 3) return 'Q2: describe the industries, titles or seniority you\'re targeting.';
    if (skills.length === 0) return 'Q3: add at least one professional skill.';
    if (problems.trim().length < 3) return 'Q5: describe the problems you help solve.';
    if (!tone) return 'Q6: choose the tone your profile should have.';
    return null;
  }, [attract, targeting, skills, problems, tone]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const r = await expertPrefsApi.save({
        attract_audience: attract,
        target_industries_titles: targeting,
        top_skills: skills,
        success_stories: stories || null,
        problems_solved: problems,
        tone,
        visitor_actions: actions,
      });
      setMeta({ submitted_at: r?.submitted_at || meta.submitted_at, updated_at: r?.updated_at || null });
      toast.success(meta.submitted_at ? 'Preferences updated.' : 'Preferences saved. Your expert-audit rewrite will use these.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save your preferences'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="preferences-page">
        <div className="flex items-start gap-3 mb-2">
          <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ClipboardList size={20} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Expert-audited preference questionnaire</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Answer the seven questions below and we&rsquo;ll rewrite your expert-audited profile to match. Questions marked with <span className="text-destructive font-medium">*</span> are required.
            </p>
            {meta.updated_at && (
              <p className="text-[11px] font-mono-edge text-muted-foreground mt-2">
                Last updated {(() => { try { return format(new Date(meta.updated_at), 'MMM d, yyyy · HH:mm'); } catch (_e) { return meta.updated_at; } })()}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <Card className="mt-6"><CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading your saved answers…
          </CardContent></Card>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 mt-6" data-testid="preferences-form">
            {/* Q1 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm">Who do you want your LinkedIn profile to attract?{REQUIRED_LABEL}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Pick the primary audience — recruiters, prospects & clients, or industry peers.</p>
              </div>
              <RadioTiles options={ATTRACT_OPTIONS} value={attract} onChange={setAttract} testId="pref-q1" />
            </CardContent></Card>

            {/* Q2 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm" htmlFor="pref-q2">Which industries, job titles, or seniority are you targeting?{REQUIRED_LABEL}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Be specific &mdash; e.g., &ldquo;Series-B B2B SaaS CROs in North America&rdquo; or &ldquo;Head of Product roles at fintech scale-ups in Singapore.&rdquo;</p>
              </div>
              <Textarea
                id="pref-q2"
                value={targeting}
                onChange={(e) => setTargeting(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Industries · titles · seniority · geography"
                data-testid="pref-q2"
              />
              <p className="text-[11px] text-muted-foreground text-right">{targeting.length}/2000</p>
            </CardContent></Card>

            {/* Q3 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm">Your top professional skills or areas of expertise{REQUIRED_LABEL}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Aim for five sharp, specific skills &mdash; press Enter or comma after each. Vague labels (&ldquo;leadership&rdquo;) work harder when narrowed (&ldquo;cross-functional GTM leadership&rdquo;).</p>
              </div>
              <SkillsInput value={skills} onChange={setSkills} testId="pref-q3" />
            </CardContent></Card>

            {/* Q4 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm" htmlFor="pref-q4">Projects, assignments, or client success stories that best demonstrate your capabilities</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Optional &mdash; a paragraph or two, ideally with measurable outcomes. We&rsquo;ll thread them through your About + Experience rewrites.</p>
              </div>
              <Textarea
                id="pref-q4"
                value={stories}
                onChange={(e) => setStories(e.target.value)}
                rows={5}
                maxLength={4000}
                placeholder="What did you build / ship / save / grow? What was the outcome?"
                data-testid="pref-q4"
              />
              <p className="text-[11px] text-muted-foreground text-right">{stories.length}/4000</p>
            </CardContent></Card>

            {/* Q5 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm" htmlFor="pref-q5">What problems do you typically help employers or clients solve?{REQUIRED_LABEL}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Name the ache &mdash; e.g., &ldquo;Series-B founders whose pipeline stalled because inbound couldn&rsquo;t scale.&rdquo;</p>
              </div>
              <Textarea
                id="pref-q5"
                value={problems}
                onChange={(e) => setProblems(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="The specific problem you solve, and for whom"
                data-testid="pref-q5"
              />
              <p className="text-[11px] text-muted-foreground text-right">{problems.length}/4000</p>
            </CardContent></Card>

            {/* Q6 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm">What tone should the profile have?{REQUIRED_LABEL}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Pick the voice that best matches how you actually sound in the room.</p>
              </div>
              <RadioTiles options={TONE_OPTIONS} value={tone} onChange={setTone} testId="pref-q6" columns={5} />
            </CardContent></Card>

            {/* Q7 */}
            <Card><CardContent className="p-5 space-y-3">
              <div>
                <Label className="text-sm">What should visitors do after viewing your profile?</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Optional &mdash; pick every action that makes sense for your goal. We&rsquo;ll tune your CTA + featured section accordingly.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pref-q7">
                {VISITOR_ACTIONS.map((a) => {
                  const checked = actions.includes(a.key);
                  return (
                    <label
                      key={a.key}
                      htmlFor={`action-${a.key}`}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                        checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-secondary/40'
                      }`}
                      data-testid={`pref-q7-row-${a.key}`}
                    >
                      <Checkbox
                        id={`action-${a.key}`}
                        checked={checked}
                        onCheckedChange={() => toggleAction(a.key)}
                        data-testid={`pref-q7-${a.key}`}
                      />
                      <span className="text-sm">{a.label}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent></Card>

            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground">
                Your answers are visible to you and Blayzrr admins only. Never shared with other users.
              </p>
              <Button
                type="submit"
                disabled={saving || !!validationError}
                className="rounded-xl min-w-[160px]"
                data-testid="preferences-submit"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-2" /> Saving…
                  </>
                ) : meta.submitted_at ? (
                  'Update preferences'
                ) : (
                  'Save preferences'
                )}
              </Button>
            </div>
            {validationError && (
              <p className="text-xs text-destructive text-right" data-testid="preferences-validation-msg">{validationError}</p>
            )}
          </form>
        )}
      </div>
    </AppShell>
  );
}
